import { Flex, Text, Image, Modal, ModalContent, ModalOverlay, useDisclosure, Box, useToast, Link } from '@chakra-ui/react'
import { Button, Column, Radio } from '@ubirider/pick-component-library'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RedirectModal } from '../RedirectModal'
import { ErrorModal } from '../ErrorModal/ErrorModal'
import { getVivaWalletPaymentInfo, settleUnpaidTrips } from '../../api/payment'
import { useCards, useTrips, useUser } from '../../context/DataProvider'
import { detectCardBrand } from '../../helpers/cards'

import CheckIcon from '../../assets/check_circle_white.svg'
import CloseIcon from '../../assets/cross_white_24.svg'
import CardIcon from '../../assets/credit_card.svg'
import { parseAmountString } from '../../helpers/currency'

export const SettleAmountModal = ({ trips, totalAmount, isOpen, onClose }) => {
  const { t } = useTranslation()

  const toast = useToast()
  const toastIdRef = useRef()

  const windowSize = useRef([window.innerWidth, window.innerHeight])

  const { user } = useUser()
  const { cards } = useCards()
  const { refetch } = useTrips()

  const { isOpen: isOpenRedirect, onOpen: onOpenRedirect, onClose: onCloseRedirect } = useDisclosure()
  const { isOpen: isOpenError, onOpen: onOpenError, onClose: OnCloseError } = useDisclosure()

  function close() {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current)
    }
  }

  const [selectedCard, selectCard] = useState(null)
  const [orderCode, setOrderCode] = useState(null)
  const [loading, setLoading] = useState(false)

  const tripSaleIDs = trips.flatMap(trip =>
    trip.invoices.map(invoice => invoice.saleID)
  )

  const retryVivaWalletPayment = async () => {
    setLoading(true)
    try {
      if (selectedCard?.cardUniqueReference) {
        const res = await settleUnpaidTrips(parseAmountString(totalAmount), selectedCard.recurringTransactionID, selectedCard.cardUniqueReference, tripSaleIDs)

        if (res.data?.StatusId === 'F') {
          await refetch()
          onClose()
          toastIdRef.current = toast({
            position: 'top',
            duration: 5000,
            isClosable: true,
            render: () => (
              <Box
                alignItems='center'
                color='white'
                bg='#01AE70'
                border='1px solid #067F5B'
                boxShadow='box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.05)'
                borderRadius='4px'
              >
                <Flex justifyContent='space-between' gap='16px' padding='16px'>
                  <Image src={CheckIcon} />
                  <Text fontSize='14px' fontWeight='600'>
                    {t('acqua_vias.amount_settled_confirmation_toast')}
                  </Text>
                  <Image cursor='pointer' onClick={close} src={CloseIcon} />
                </Flex>
              </Box>
            ),
          })
        } else {
          onOpenError()
        }
      } else {
        const data = { ...user, amount: totalAmount * 100 }
        const res = await getVivaWalletPaymentInfo(data, false)
        setOrderCode(res.data.orderCode)
        onOpenRedirect()
      }
    } catch (e) {
      console.error(e)
      onOpenError()
    }
    setLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      trapFocus={false}
      closeOnOverlayClick={false}
      autoFocus={false}
      scrollBehavior='inside'
      size={{ sm: 'full', md: 'full', lg: '', xl: '' }}
      isCentered={windowSize.current[0] >= 960}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius='8px'
        maxW={{ lg: '600px' }}
        padding={{ sm: '16px 24px', md: '16px 24px', lg: '36px', xl: '36px' }}
      >
        <Flex direction='column' gap='32px' h="100%" mt={{ sm: '67px', md: '67px', lg: '0px', xl: '0px' }}>
          <Box flexShrink={0}>
          <Flex
            direction='column'
            gap='8px'
            align={{ sm: 'center', lg: 'flex-start' }}
            justify={{ sm: 'center', lg: 'flex-start' }}
            textAlign={{ sm: 'center', lg: 'left' }}
          >
            <Text fontSize={{ sm: '20px', lg: '24px' }} fontWeight='700'>
              {t('acqua_vias.settle_pending_amount_dialog_title')}
            </Text>
            <Text fontSize={{ sm: '14px', lg: '16px' }}>
              {t('acqua_vias.settle_pending_amount_dialog_description')}
            </Text>
          </Flex>
          </Box>

          <Column css={{ gap: 16 }} flex="1" overflow="hidden">
            <Text fontWeight='600' fontSize={{ sm: '14px', lg: '16px' }}>
              {t('acqua_vias.settle_pending_amount_dialog_choose_payment_method')}
            </Text>
            <Box
              maxH={{ base: 'none', lg: '350px' }}
              overflowY='auto'
              border='1px solid #EAECF0'
              borderRadius='16px'
              w='100%'
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#EAECF0',
                  borderRadius: '10px',
                  border: '16px solid white',
                },
                // Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: '#EAECF0 transparent',
              }}
            >
              <Flex direction='column' padding='8px'>
                {cards.map((card, index) => (
                  <Flex
                    cursor='pointer'
                    align='center'
                    gap='16px'
                    padding='16px'
                    borderBottom={index === cards.length - 1 ? 'none' : '1px solid #EAECF0'}
                    key={`${index}_lg`}
                    onClick={() => selectCard(card)}
                  >
                    <Radio
                      checked={selectedCard?.cardUniqueReference === card.cardUniqueReference}
                      onClick={() => selectCard(card)}
                    />
                    <Column>
                      <Flex gap='16px' align='center'>
                        <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='700'>
                          {detectCardBrand(card.number.slice(0, 6)).label} •••• {card.number.slice(-4)}
                        </Text>
                      </Flex>
                      <Text
                        fontSize={{ sm: '14px', lg: '16px' }}
                        fontWeight='600'
                        color='neutral.600'
                      >{`${card.expiration.substring(5, 7)}/${card.expiration.substring(2, 4)}`}</Text>
                    </Column>
                  </Flex>
                ))}
                <Flex cursor='not-allowed' align='center' gap='16px' padding='16px' borderTop='1px solid #EAECF0'>
                  <Radio checked={selectedCard === 'bank_account'} disabled />
                  <Column>
                    <Flex gap='16px' align='center'>
                      <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='700' color='neutral.300'>
                        {t('acqua_vias.settle_pending_amount_dialog_bank_account')}
                      </Text>
                    </Flex>
                    <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='600' color='neutral.300'>
                      {t('acqua_vias.settle_pending_amount_dialog_business_days', { days: '1-2' })}
                    </Text>
                  </Column>
                </Flex>
              </Flex>
            </Box>
          </Column>

          <Box flexShrink={0} mt="auto">
          <Flex
            w='100%'
            gap='16px'
            justifyContent='flex-end'
            display={{ sm: 'none', md: 'none', lg: 'flex', xl: 'flex' }}
          >
            <Button
              onClick={onClose}
              style='outline'
              type='neutralDark'
              label={t('common.cancel')}
              css={{ width: 152 }}
            />
            <Button
              onClick={retryVivaWalletPayment}
              loading={loading}
              style='solid'
              type='secondary'
              label={t('acqua_vias.pay_amount', { amount: `R$ ${totalAmount}` })}
              leftIconProps={{ src: CardIcon }}
            />
          </Flex>
          <Flex
            gap='16px'
            alignSelf='stretch'
            direction='column'
            display={{ sm: 'flex', md: 'flex', lg: 'none', xl: 'none' }}
          >
            <Button
              onClick={retryVivaWalletPayment}
              loading={loading}
              style='solid'
              type='secondary'
              label={t('acqua_vias.pay_amount', { amount: `R$ ${totalAmount}` })}
              leftIconProps={{ src: CardIcon }}
            />
            <Button
              onClick={onClose}
              style='outline'
              type='neutralDark'
              label={t('common.cancel')}
              css={{ width: '100%' }}
            />
          </Flex>
          </Box>
        </Flex>
      </ModalContent>

      <RedirectModal orderCode={orderCode} isOpen={isOpenRedirect} onOpen={onOpenRedirect} onClose={onCloseRedirect} />
      <ErrorModal
        onConfirm={retryVivaWalletPayment}
        loading={loading}
        title={t('acqua_vias.payment_failed_dialog_title')}
        description={t('acqua_vias.payment_failed_dialog_description')}
        isOpen={isOpenError}
        onOpen={onOpenError}
        onClose={OnCloseError}
      />
    </Modal>
  )
}
